import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Platform } from '@ionic/angular';
import { Preferences } from '@capacitor/preferences';
import OneSignal from 'onesignal-cordova-plugin';
import { BehaviorSubject, tap } from 'rxjs';
import { BaseService } from './base.service';
import { UserTransformer } from '../utils/transformers/user.transformer';
import * as ApiLogin from '../interfaces/api/login';
import * as ApiForgotPassword from '../interfaces/api/forgot-password';
import * as ApiRefreshToken from '../interfaces/api/refresh-token';
import * as ApiRegister from '../interfaces/api/register';
import * as ApiUser from '../interfaces/api/user';
import * as ApiCheckAccount from '../interfaces/api/checkaccount';
import * as ApiPersonalInfo from '../interfaces/api/personalinfo';
import * as ApiCompanyInfo from '../interfaces/api/companyinfo';
import { User, responseUser } from '../interfaces/user';
import { Capacitor } from '@capacitor/core';

const ACCESS_TOKEN_KEY = 'accessToken';

@Injectable({
  providedIn: 'root',
})
export class AuthService extends BaseService {
  private _user = new BehaviorSubject<User>(null);

  constructor(private http: HttpClient, private userTransformer: UserTransformer) {
    super();
  }

  get user() {
    return this._user.asObservable();
  }

  changeOrderDataType(orderDataType: number) {
    return this.http.post<responseUser>(`${this.apiUrl}/profile/change_order_data_type`, {
      order_data_type: orderDataType,
    });
  }

  getProfile() {
    return this.http.get<responseUser>(`${this.apiUrl}/profile`);
  }

  changeProfileName(data: any) {
    return this.http.post(`${this.apiUrl}/profile`, data);
  }

  async isAuthenticated() {
    const accessToken = await this.getAccessToken();

    return !!accessToken;
  }

  async setAccessToken(accessToken: string) {
    await Preferences.set({
      key: ACCESS_TOKEN_KEY,
      value: accessToken,
    });
  }

  async removeAccessToken() {
    await Preferences.remove({
      key: ACCESS_TOKEN_KEY,
    });
  }

  async getAccessToken(): Promise<string | null> {
    const { value } = await Preferences.get({
      key: ACCESS_TOKEN_KEY,
    });

    return value || null;
  }

  setUser(user: User | null) {
    this._user.next(user);
  }

  unsetUser() {
    this.setUser(null);
  }

  register(payload: ApiRegister.RequestBody) {
    return this.http.post<ApiRegister.ResponseBody>(`${this.apiUrl}/register`, payload).pipe(
      tap(async response => {
        await this.setAccessToken(response.token);

        this.setUser(this.userTransformer.transform(response.user));

        if (Capacitor.isNativePlatform()) {
          this.registerOneSignalUserId();
        }
      }),
    );
  }

  login(payload: ApiLogin.RequestBody) {
    return this.http.post<ApiLogin.ResponseBody>(`${this.apiUrl}/login`, payload).pipe(
      tap(async response => {
        await this.setAccessToken(response.token);

        this.setUser(this.userTransformer.transform(response.user));

        if (Capacitor.isNativePlatform()) {
          this.registerOneSignalUserId();
        }
      }),
    );
  }

  loginSocial(payload: ApiLogin.RequestBodySocial) {
    return this.http.post<ApiLogin.LoginResponse>(`${this.apiUrl}/auth/social`, payload).pipe(
      tap(async response => {
        console.log(response);
        await this.setAccessToken(response.token);

        this.setUser(response.user);

        if (Capacitor.isNativePlatform()) {
          this.registerOneSignalUserId();
        }
      }),
    );
  }

  checkAccount(payload: ApiCheckAccount.RequestBody) {
    return this.http.post<ApiCheckAccount.ResponseBody>(`${this.apiUrl}/check_account`, payload);
  }

  getUser() {
    return this.http.get<ApiUser.ResponseBodyAuth>(`${this.apiUrl}/profile`).pipe(
      tap(async response => {
        this.setUser(this.userTransformer.transform(response.user));
      }),
    );
  }

  logout() {
    this.removeAccessToken();
    this.unsetUser();
  }

  refreshToken() {
    return this.http.post<ApiRefreshToken.ResponseBody>(`${this.apiUrl}/refresh`, null).pipe(
      tap(async response => {
        await this.setAccessToken(response.access_token);
      }),
    );
  }

  updateProfileFormData(payload: any) {
    const headers = new HttpHeaders();
    headers.set('Content-Type', 'multipart/form-data');
    return this.http.post<ApiUser.ResponseBodyAuth>(`${this.apiUrl}/profile`, payload, { headers }).pipe(
      tap(response => {
        this.setUser(this.userTransformer.transform(response.user));
      }),
    );
  }

  updateProfile(payload: ApiUser.RequestBody) {
    return this.http.post<ApiUser.ResponseBodyAuth>(`${this.apiUrl}/profile`, payload).pipe(
      tap(response => {
        this.setUser(this.userTransformer.transform(response.user));
      }),
    );
  }

  updatePersonalInfo(payload: ApiPersonalInfo.RequestBody) {
    return this.http.post<ApiUser.ResponseBodyAuth>(`${this.apiUrl}/profile/personal`, payload).pipe(
      tap(response => {
        this.setUser(this.userTransformer.transform(response.user));
      }),
    );
  }

  updateCompanyInfo(payload: ApiCompanyInfo.RequestBody) {
    return this.http.post<ApiUser.ResponseBodyAuth>(`${this.apiUrl}/profile/company`, payload).pipe(
      tap(response => {
        this.setUser(this.userTransformer.transform(response.user));
      }),
    );
  }

  changeForgotPassword(payload: ApiForgotPassword.RequestBodyChangeForgotPassword) {
    return this.http.post<ApiForgotPassword.ResponseBody>(`${this.apiUrl}/forgot-password`, payload);
  }

  changeEmail(email: string) {
    return this.http.post(`${this.apiUrl}/profile/change-email?type=mobile`, {
      email,
      type: 'mobile',
    });
  }

  changeTelp(phone: string) {
    return this.http.post(`${this.apiUrl}/profile/change-telp?type=mobile`, {
      telp_home: phone,
      type: 'mobile',
    });
  }

  verifyEmail(email: string) {
    return this.http.post(`${this.apiUrl}/request-link?type=mobile`, {
      email,
      type: 'mobile',
    });
  }

  deleteAccount() {
    return this.http.post(`${this.apiUrl}/delete-account`, {
      // responseType: 'text',
    });
  }

  private registerOneSignalUserId() {
    OneSignal.getDeviceState(({ userId }) =>
      this.http
        .post(`${this.apiUrl}/onesignal`, {
          uid: userId,
        })
        .subscribe(),
    );
  }
}
